/**
 * AssetsOverviewPage — IVT Hub "Assets" Overview dashboard (`/ivt/assets`).
 *
 * Figma re-design (asset-domain-v2-design.md §2). A full-width dashboard over ALL
 * assets (every origin — NOT the ivt_incubated-only slice the old NewAssetsPage
 * showed): toolbar (origin filter + search + create) → KPI band → maturity
 * distribution → industry swimlane matrix. All aggregation is client-side from a
 * single ListAssets({}) call.
 *
 * NEW page — does NOT touch NewAssetsPage (which stays live until M4 deprecation).
 * View by Industry + View by Topic (§2.6) are both implemented; TITLE KPI (§2.3)
 * remains a placeholder (PM undefined).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Spinner } from '@amzn/awsui-components-react';
import PageHeader from '../../../components/PageHeader/PageHeader';
import NewAssetCreateModal from '../NewAssetsPage/components/NewAssetCreateModal';
import { useAssetsData } from './hooks/useAssetsData';
import { useStorylineTreeProps } from './hooks/useStorylineTreeProps';
import { byIndustry, byTopic, byMaturity, computeKpis } from './utils/assetGrouping';
import { getOriginFilter, loadOriginFilterId, saveOriginFilterId } from './utils/originFilters';
import AssetsToolbar from './components/AssetsToolbar';
import AssetsKpiRow from './components/AssetsKpiRow';
import MaturityLegend from './components/MaturityLegend';
import { loadAssetsView, saveAssetsView } from './components/AssetsViewSwitch';
import AssetsIndustryMatrix from './components/AssetsIndustryMatrix';
import AssetsTopicMatrix from './components/AssetsTopicMatrix';
import AssetNavTree from './components/AssetNavTree';
import CollapsibleTreePanel from './components/CollapsibleTreePanel';
import './AssetsOverviewPage.css';

export default function AssetsOverviewPage() {
  const navigate = useNavigate();
  const { assets, industries, loading, error, reload } = useAssetsData();

  // Origin filter persists across in-app navigation (seed from sessionStorage).
  const [originId, setOriginId] = useState(loadOriginFilterId);
  const [search, setSearch] = useState('');
  const [view, setView] = useState(loadAssetsView);
  const [creating, setCreating] = useState(false);

  const all = useMemo(() => assets ?? [], [assets]);

  // KPI band is ALWAYS over the full set (a global overview — the origin/search
  // filters do not move TOTAL / NEW / 本月新增).
  const kpis = useMemo(() => computeKpis(all), [all]);

  // Matrix respects origin filter + name search (both client-side, orthogonal).
  const filtered = useMemo(() => {
    const originMatch = getOriginFilter(originId).match;
    const q = search.trim().toLowerCase();
    return all.filter((a) => {
      if (!originMatch(a)) return false;
      if (!q) return true;
      const name = (a.name ?? a.useCase ?? a.assetId ?? '').toLowerCase();
      return name.includes(q);
    });
  }, [all, originId, search]);

  // Maturity distribution (the legend) tracks the FILTERED set so its counts match
  // the cards currently shown (e.g. picking "New Assets" recounts over incubated
  // only). KPI stays on the full set above.
  const maturityBuckets = useMemo(() => byMaturity(filtered), [filtered]);

  // Column set is driven by the industry UNIVERSE (ListIndustries) so every
  // industry shows — including empty ones (e.g. ECOM with 0 assets). Topic-anchored
  // (no-industry) assets are excluded from View by Industry.
  const industryOrder = useMemo(
    () => (industries ?? []).map((i) => i.industry).filter((n): n is string => !!n),
    [industries],
  );
  const columns = useMemo(() => byIndustry(filtered, industryOrder), [filtered, industryOrder]);
  // By-Topic rows (same filtered set); topic-anchored assets grouped by topic.
  const topicRows = useMemo(() => byTopic(filtered), [filtered]);

  // All Storyline-Menu tree wiring (skeleton fetch + reload, industry universe,
  // storyline-node navigation, Create modal) in one place — shared by every
  // tree-hosting page so none can forget a prop.
  const storylineTree = useStorylineTreeProps({ assets: all, industries, reloadAssets: reload });

  return (
    <div className="ivt-assets-overview">
      <PageHeader title="Assets" />

      {creating && (
        <NewAssetCreateModal
          onClose={() => setCreating(false)}
          onCreated={(assetId) => {
            // Per §4.2: navigate to the new asset's detail page (Create-then-fill flow).
            setCreating(false);
            navigate(`/ivt/assets/${assetId}`);
          }}
        />
      )}

      {/* Create Storyline modal — opened by the Storyline Menu tree's industry-row `+`
          (see useStorylineTreeProps). */}
      {storylineTree.modalNode}

      {/* Two-column shell: shared collapsible category tree (left) + the dashboard
          (right). The tree is the SAME AssetNavTree as the Detail page — leaf asset
          click navigates to that asset's detail. */}
      <div className="ivt-assets-overview__shell">
        <CollapsibleTreePanel>
          <AssetNavTree
            assets={all}
            selectedAssetId={null}
            onSectionClick={(v) => {
              // Already on the Overview — just switch the live matrix view (no nav);
              // persist so it sticks. Handles the "same route, no remount" case.
              setView(v);
              saveAssetsView(v);
            }}
            onUseCaseClick={(id) => navigate(`/ivt/use-cases/${id}`)}
            // Storyline Menu mode: skeleton + industry universe + storyline-node nav +
            // Create `+`, all from the shared hook (one assembly, every page).
            {...storylineTree.treeProps}
          />
        </CollapsibleTreePanel>

        <div className="ivt-assets-overview__main">
          <AssetsToolbar
            originId={originId}
            onOriginChange={(id) => {
              setOriginId(id);
              saveOriginFilterId(id); // persist so it survives navigating away + back
            }}
            search={search}
            onSearchChange={setSearch}
            onCreate={() => setCreating(true)}
          />

          {loading && (
            <div className="ivt-assets-overview__loading">
              <Spinner size="large" />
              <span>加载 Assets…</span>
            </div>
          )}

          {!loading && error && (
            <Alert type="error" header="无法加载 Assets" action={<button onClick={reload}>重试</button>}>
              {error}
            </Alert>
          )}

          {!loading && !error && (
            <>
              <AssetsKpiRow kpis={kpis} />

              <section className="ivt-assets-overview__matrix-section">
                <div className="ivt-assets-overview__matrix-head">
                  <h2 className="ivt-assets-overview__matrix-title">Assets</h2>
                  <MaturityLegend buckets={maturityBuckets} />
                  {/* The View by Industry / Topic segmented control was removed — the
                      left category tree's clickable Industry / Topic section labels
                      are now the view switch (a stored `view` still drives the matrix
                      + persists). Expand the tree if collapsed to switch. */}
                </div>

                {view === 'industry' ? (
                  <AssetsIndustryMatrix columns={columns} />
                ) : (
                  <AssetsTopicMatrix rows={topicRows} />
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
